import type { TFunction } from 'i18next';

import type { DsiSourceInfo } from './live-preview';

/**
 * Game lists as the brand's DEPLOYED site serves them, read over the ds-inspector bridge (the same
 * sanctioned `dsi-*` channel the banner preview uses). Pure helpers only: no React, no messaging.
 *
 * There is no owner-facing game-list API yet (SPB-324/325), so the site itself is the source: each game
 * list is a `slot_game` data source in the site store, and a reordered list is previewed by pinning its
 * full content back over the same data source. See `.claude/specs/2026-09-16-creatives-game-lists.md`.
 */

/** A game row as the site's `slot_game` source serves it. Only the fields this area reads are typed; the
 *  rest is carried through untouched, so a pinned list renders exactly like the live one (the pin skips the
 *  site's own hydration, so an id-only row would render as an empty tile). */
export type SiteGame = {
  id?: string | number;
  _id?: string | number;
  name?: string;
  provider_id?: string | number;
  art_bundle?: { data?: Record<string, unknown> };
  [key: string]: unknown;
};

/** A data source's content as the site store holds it (`dsi-get-source` / `dsi-set-source`). */
export type SiteSourceContent = {
  element_type?: unknown;
  meta?: unknown;
  data: unknown[];
  [key: string]: unknown;
};

/** One game list on a site page: its data-source id and how many games it holds. */
export type SiteGameList = { id: string; count: number };

/** A page of the brand's site that carries game lists, named by the path the site serves it at ("/",
 *  "/casino", "/live-casino"). Which pages a site has is the service's answer (see `site-pages-api`). */
export type GamePage = string;

/** The home page, which every brand has: where the tab opens, and all it offers when the service cannot
 *  say which pages the site has. */
export const HOME_PAGE: GamePage = '/';

/** The type a game list's source reports, and the one the service names a page's game lists by (`list_type`). */
export const GAME_TYPE_ID = 'slot_game';
const PROVIDER_TYPE_ID = 'casino_game_provider';
/** The whole game catalogue, not a curated list: never offered for reordering, never pinned. */
const CATALOGUE_IDS = new Set(['all_slot_games_api', 'all-games']);
/**
 * Game lists the platform serves wrongly, withheld from the tab until it is fixed. The jackpot lists
 * (`jackpot_games_*`) filter on `metadata_game_attribute_jackpot_name_id`, which no game carries, so four of
 * them return the whole catalogue and `jackpot_games_egt` returns nothing. The owner chose to hide them rather
 * than offer a "list" that is really every game. Remove this rule once the platform's jackpot data is fixed
 * (spark-creatives withholds the matching site pages under the same rule).
 */
const WITHHELD_LIST_PREFIXES = ['jackpot_games_'];

/** Whether a game list is one the platform serves wrongly and the tab withholds (see above). */
export const isWithheldPlatformList = (id: string): boolean =>
  WITHHELD_LIST_PREFIXES.some((prefix) => id.startsWith(prefix));

/** Whether the tab offers a game list by its id: neither the whole catalogue nor a withheld list. The one rule for
 *  every reader of the site's lists (the tab's chips, the Website hub's Game Lists card). */
export const isOfferedGameList = (id: string): boolean => !CATALOGUE_IDS.has(id) && !isWithheldPlatformList(id);

/** Art variants to show on a square tile, best first. */
const THUMB_KEYS = ['square_ar11_var_keycenter', 'portrait_ar23_var_keycenter', 'landscape_ar32_var_keycenter'];

/** The page's reorderable game lists, in the order the site store reports them. */
export const gameListsFromSources = (sources: DsiSourceInfo[]): SiteGameList[] =>
  sources
    .filter((s) => s.typeId === GAME_TYPE_ID && typeof s.count === 'number' && s.count > 0 && isOfferedGameList(s.id))
    .map((s) => ({ id: s.id, count: s.count as number }));

/** The id of the page's whole-catalogue game source (all games, not a curated list), or null when the page
 *  has none. This is the pool a manual custom list picks its games from. */
export const catalogueSourceId = (sources: DsiSourceInfo[]): string | null =>
  sources.find((s) => s.typeId === GAME_TYPE_ID && CATALOGUE_IDS.has(s.id))?.id ?? null;

/** The id of the page's game-provider source (id → display name), or null when the page has none. */
export const providerSourceId = (sources: DsiSourceInfo[]): string | null =>
  sources.find((s) => s.typeId === PROVIDER_TYPE_ID)?.id ?? null;

/** Narrow an untrusted bridge payload to source content (an object with a `data` array). */
export const asSourceContent = (value: unknown): SiteSourceContent | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return Array.isArray((value as { data?: unknown }).data) ? (value as SiteSourceContent) : null;
};

/** How many games a list holds in all, when its content says so (`meta.total`), rather than how many rows
 *  the site has loaded so far. Null when the content gives no total: the caller keeps the loaded count. */
export const sourceTotal = (content: SiteSourceContent | undefined): number | null => {
  const meta = content?.meta;
  if (typeof meta !== 'object' || meta === null) return null;
  const total = (meta as { total?: unknown }).total;
  return typeof total === 'number' && Number.isFinite(total) && total >= 0 ? total : null;
};

/** A game's stable identity, the same `id ?? _id` the site's item markers use. */
export const gameKey = (game: SiteGame): string => String(game.id ?? game._id ?? '');

/** The rows of a source that are usable games (objects carrying an id). */
export const gamesOf = (content: SiteSourceContent): SiteGame[] =>
  content.data.filter(
    (row): row is SiteGame => typeof row === 'object' && row !== null && gameKey(row as SiteGame) !== '',
  );

/** Provider id → display name, from the provider source's content. */
export const providerNamesFromContent = (content: SiteSourceContent | null): Record<string, string> => {
  const names: Record<string, string> = {};
  if (!content) return names;
  for (const row of content.data) {
    if (typeof row !== 'object' || row === null) continue;
    const { id, display_name: displayName, name } = row as { id?: unknown; display_name?: unknown; name?: unknown };
    const label =
      typeof displayName === 'string' && displayName.trim()
        ? displayName.trim()
        : typeof name === 'string'
          ? name.trim()
          : '';
    if ((typeof id === 'string' || typeof id === 'number') && label) names[String(id)] = label;
  }
  return names;
};

/** A readable name for a list from its data-source id (`spk-new-games` → `New games`): the fallback for a
 *  list the page gives no title of its own (see `section-titles.ts`, which reads the page's titles). */
export const gameListLabel = (id: string): string => {
  const words = id
    .replace(/^spk[-_]/i, '')
    .split(/[-_.\s]+/)
    .filter(Boolean)
    .map((word) => word.toLowerCase());
  if (words.length === 0) return id;
  const sentence = words.join(' ');
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
};

const isHttpUrl = (value: unknown): value is string => typeof value === 'string' && /^https?:\/\//i.test(value);

/** The tile image for a game: the square art when present, else any art URL. The CDN paths carry the
 *  game's name with raw spaces, which are not valid in a URL, so they are encoded. */
export const gameThumbUrl = (game: SiteGame): string => {
  const data = game.art_bundle?.data ?? {};
  const url = THUMB_KEYS.map((key) => data[key]).find(isHttpUrl) ?? Object.values(data).find(isHttpUrl);
  return url ? url.replace(/ /g, '%20') : '';
};

/** Two id orders are equal when they have the same ids in the same positions. */
export const sameOrder = (a: string[], b: string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);

/**
 * Whether the order the owner published still holds on the site: their games lead the list, in their
 * order, ignoring anything the platform has since added or removed around them.
 *
 * A strict equality check cannot be used for that question. The published override positions the owner's
 * games and LEAVES THE REST IN, so a game the platform adds to the list afterwards appears after them.
 * Against `sameOrder` that reads as the owner having unpublished changes, and re-publishing cannot clear
 * it: the same order goes out, the new game still lands at the end, and the comparison fails again.
 */
export const orderHolds = (working: string[], live: string[]): boolean => {
  // An empty live order is NO INFORMATION, not an empty site list: the tab reaches it before the list has
  // loaded and whenever it cannot be read at all. Answering "holds" there would report a list with a saved
  // draft as having nothing to publish, and drop it from the count that would have recovered it.
  if (live.length === 0) return working.length === 0;
  const inLive = new Set(live);
  // Only the owner's games the list still has: one the platform dropped is not their change.
  const kept = working.filter((id) => inLive.has(id));
  return kept.every((id, at) => live[at] === id);
};

/** The games in `ids` order. Unknown ids and repeats are skipped; games the order does not mention keep
 *  their relative order at the end, so a stale order never drops a game. */
export const orderedGames = (games: SiteGame[], ids: string[]): SiteGame[] => {
  const byKey = new Map(games.map((game) => [gameKey(game), game]));
  const seen = new Set<string>();
  const out: SiteGame[] = [];
  const take = (key: string): void => {
    const game = byKey.get(key);
    if (game && !seen.has(key)) {
      seen.add(key);
      out.push(game);
    }
  };
  ids.forEach(take);
  games.forEach((game) => take(gameKey(game)));
  return out;
};

/** The content to pin over a list's data source: the original envelope with its games in `ids` order. */
export const pinnedContent = (original: SiteSourceContent, ids: string[]): SiteSourceContent => ({
  ...original,
  data: orderedGames(gamesOf(original), ids),
});

/** Replace the first `head.length` ids of `ids` with `head` (a reordering of that shown window), keeping
 *  the ids past the window in place. */
export const replaceHead = (ids: string[], head: string[]): string[] => [...head, ...ids.slice(head.length)];

/** `ids` with `id` moved to index `to` (clamped to the list); an id the order does not hold leaves it as is. */
export const moveId = (ids: string[], id: string, to: number): string[] => {
  const from = ids.indexOf(id);
  if (from === -1) return ids;
  const next = ids.filter((_, index) => index !== from);
  next.splice(Math.max(0, Math.min(to, next.length)), 0, id);
  return next;
};

/** A game's provider as the site names it, or a readable fallback when the provider source does not. */
export const providerLabelOf = (game: SiteGame, providerNames: Record<string, string>, t: TFunction): string => {
  const id = game.provider_id;
  if (id === undefined || id === null || id === '') return t('Unknown provider');
  return providerNames[String(id)] ?? t('Provider {{id}}', { id: String(id) });
};

/** Lower-case and accent-free, so a filter for "creme" finds "Crème". */
export const foldText = (value: string): string => value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** A filter query as the words a game has to contain. */
export const filterTerms = (query: string): string[] => foldText(query).split(/\s+/).filter(Boolean);

/** Whether every filter word appears in the game's name, ID or provider name (no words: every game does). */
export const gameMatches = (game: SiteGame, provider: string, terms: string[]): boolean => {
  if (terms.length === 0) return true;
  const haystack = foldText(`${game.name ?? ''} ${gameKey(game)} ${provider}`);
  return terms.every((term) => haystack.includes(term));
};

/** The match one `step` from `current`, wrapping at both ends; from no current match, the first or the last. */
export const stepMatch = (matches: string[], current: string | null, step: 1 | -1): string | null => {
  if (matches.length === 0) return null;
  const at = current === null ? -1 : matches.indexOf(current);
  if (at === -1) return step === 1 ? matches[0] : matches[matches.length - 1];
  return matches[(at + step + matches.length) % matches.length];
};

/**
 * The match to step to from where the page is scrolled. `offsets` holds, per match in list order, how far
 * below (+) or above (-) the landing line (where a scrolled-to match sits) the game is, or null when it is
 * not rendered yet (past the rendered window, so below everything). Next is the first match below the line,
 * previous the last one above it; with none that way, it wraps to the first / last match.
 */
export const matchFromScroll = (
  matches: string[],
  offsets: (number | null)[],
  step: 1 | -1,
  tolerance = 2,
): string | null => {
  if (matches.length === 0) return null;
  if (step === 1) {
    const below = offsets.findIndex((offset) => offset === null || offset > tolerance);
    return matches[below === -1 ? 0 : below];
  }
  for (let i = offsets.length - 1; i >= 0; i -= 1) {
    const offset = offsets[i];
    if (offset !== null && offset < -tolerance) return matches[i];
  }
  return matches[matches.length - 1];
};

/** Where a rendered match's top and bottom edges are, relative to the landing line (see `matchFromScroll`). */
export type MatchSpan = { top: number; bottom: number };

/**
 * The match a keystroke jumps to: the first one from where the page is, on down. That is the first match
 * whose bottom edge is still below the landing line (one sitting on the line, or partly under the pinned
 * filter, is where the page is), or not rendered yet (past the rendered window, so below everything). With
 * none from here on, it wraps to the first match.
 */
export const firstMatchFromHere = (matches: string[], spans: (MatchSpan | null)[], tolerance = 2): string | null => {
  const here = spans.findIndex((span) => span === null || span.bottom > tolerance);
  return matches[here === -1 ? 0 : here] ?? null;
};

/**
 * A reorder of the ids that could be SHOWN, merged back into the full stored order.
 *
 * This exists because the two are not the same list. A brand's own game list is stored as ids, and the tab
 * can only draw the ones the game catalogue still holds: a game the platform has delisted is in the list but
 * cannot be rendered, so it cannot be dragged either. Every write built from what is on screen therefore
 * loses it, and that loss is silent, because the owner never saw it.
 *
 * So a reorder is applied to the STORED order rather than replacing it. Ids that could not be shown keep
 * their index; the shown ids fill the remaining places in their new order. An id in the new order that the
 * stored list does not have is appended, so this also serves a list that gained a game.
 *
 * It REORDERS, it does not replace: an id the new order omits stays where it was. A removal therefore
 * cannot go through here, and the editor's save, which can remove games, does not use it.
 */
export const mergeShownOrder = (stored: string[], shownInNewOrder: string[]): string[] => {
  const shown = new Set(shownInNewOrder);
  // Holes are where a shown id used to be; anything else stays exactly where it is.
  const slots: (string | null)[] = stored.map((id) => (shown.has(id) ? null : id));
  let at = 0;
  for (const id of shownInNewOrder) {
    while (at < slots.length && slots[at] !== null) at += 1;
    if (at >= slots.length) slots.push(id);
    else slots[at] = id;
    at += 1;
  }
  return slots.filter((id): id is string => id !== null);
};
